/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import StartScreen from './components/StartScreen';
import Canvas from './components/Canvas';
import HistoryPanel from './components/HistoryPanel';
import OutfitStack from './components/OutfitStack';
import Navigation from './components/Navigation';
import UserProfile from './components/UserProfile';
import AdminDashboard from './components/AdminDashboard';
import { generateVirtualTryOnImageFal as generateVirtualTryOnImage, generatePoseVariationFal as generatePoseVariation } from './services/falService';
import { OutfitLayer, WardrobeItem } from './types';
import { ChevronDownIcon, ChevronUpIcon } from './components/icons';
import { defaultWardrobe } from './wardrobe';
import Spinner from './components/Spinner';
import { useAuth } from './contexts/AuthContext';
import { TryOnResult } from './services/db';
import HistoryViewPage from './components/HistoryViewPage';
import {
  getProducts,
  seedProducts,
  getUserFavorites,
  toggleFavorite,
  getUserStats,
  incrementTryOnCount,
  addUserCredits,
  UserStats,
  saveTryOnResult,
  subscribeToUserStats
} from './services/db';
import Footer from './components/Footer';
import { getFriendlyErrorMessage } from './lib/utils';
import SubscriptionModal from './components/SubscriptionModal';

const POSE_INSTRUCTIONS = [
  "Full frontal view, hands on waist",
  "3/4 side view",
  "Full side view",
  "Jump/action shot",
  "Walking towards camera",
  "Leaning against wall",
];

const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const listener = (event: MediaQueryListEvent) => setMatches(event.matches);

    mediaQueryList.addEventListener('change', listener);

    if (mediaQueryList.matches !== matches) {
      setMatches(mediaQueryList.matches);
    }

    return () => {
      mediaQueryList.removeEventListener('change', listener);
    };
  }, [query, matches]);

  return matches;
};


interface CartItem {
  product: WardrobeItem;
  quantity: number;
  selectedSize: string;
  selectedColor: string;
}

const App: React.FC = () => {
  const [modelImageUrl, setModelImageUrl] = useState<string | null>(null);
  const [outfitHistory, setOutfitHistory] = useState<OutfitLayer[]>([]);
  const [currentOutfitIndex, setCurrentOutfitIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentPoseIndex, setCurrentPoseIndex] = useState(0);
  const [isSheetCollapsed, setIsSheetCollapsed] = useState(false);
  const [wardrobe, setWardrobe] = useState<WardrobeItem[]>(defaultWardrobe);
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [userStats, setUserStatsState] = useState<UserStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isAdminPageOpen, setIsAdminPageOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [viewingHistoryItem, setViewingHistoryItem] = useState<TryOnResult | null>(null);
  const { user } = useAuth();

  // Load Initial Data and User Data
  useEffect(() => {
    async function initData() {
      try {
        await seedProducts(defaultWardrobe);
        const products = await getProducts();
        if (products.length > 0) {
          setWardrobe(products);
        }
      } catch (error) {
        console.error("Failed to load products", error);
      }
    }
    initData();
  }, []);

  // Load User Specific Data (Favs & Stats) - Realtime
  useEffect(() => {
    let unsubscribeStats: (() => void) | undefined;

    async function loadUserData() {
      if (user) {
        // Initial Loading State: We can set this to true, but subscription usually fires fast with cache
        // If we want faster perceived speed, we can assume false or set false on first update.
        setIsLoadingStats(true);

        try {
          console.log("App: Loading user data...");

          // 1. Fetch Favorites (One-time, or could be realtime too)
          getUserFavorites(user.uid).then(favs => setFavorites(new Set(favs)));

          // 2. Subscribe to Stats (Realtime)
          unsubscribeStats = subscribeToUserStats(user.uid, (stats) => {
            setUserStatsState(stats);
            setIsLoadingStats(false); // Stop loading as soon as we get first data
            console.log("App: Stats updated:", stats);
          });

        } catch (error) {
          console.error("Error setting up user data:", error);
          setIsLoadingStats(false);
        }
      } else {
        setFavorites(new Set());
        setUserStatsState({ tryOnCount: 0, allowedTryOnCount: 3, isSubscribed: false });
        setIsLoadingStats(false);
      }
    }

    loadUserData();

    return () => {
      if (unsubscribeStats) unsubscribeStats();
    };
  }, [user]);



  const handleToggleFavorite = async (productId: string) => {
    if (!user) {
      setIsUserProfileOpen(true);
      return;
    }

    const isFav = favorites.has(productId);
    const newFavs = new Set(favorites);
    if (isFav) {
      newFavs.delete(productId);
    } else {
      newFavs.add(productId);
    }
    setFavorites(newFavs);

    try {
      await toggleFavorite(user.uid, productId, !isFav);
    } catch (error) {
      console.error("Error toggling favorite:", error);
      // Rollback on error
      setFavorites(favorites);
    }
  };
  const isMobile = useMediaQuery('(max-width: 767px)');

  const activeOutfitLayers = useMemo(() =>
    outfitHistory.slice(0, currentOutfitIndex + 1),
    [outfitHistory, currentOutfitIndex]
  );

  const activeGarmentIds = useMemo(() =>
    activeOutfitLayers.map(layer => layer.garment?.id).filter(Boolean) as string[],
    [activeOutfitLayers]
  );

  const displayImageUrl = useMemo(() => {
    if (outfitHistory.length === 0) return modelImageUrl;
    const currentLayer = outfitHistory[currentOutfitIndex];
    if (!currentLayer) return modelImageUrl;

    const poseInstruction = POSE_INSTRUCTIONS[currentPoseIndex];
    return currentLayer.poseImages[poseInstruction] ?? Object.values(currentLayer.poseImages)[0];
  }, [outfitHistory, currentOutfitIndex, currentPoseIndex, modelImageUrl]);

  const availablePoseKeys = useMemo(() => {
    if (outfitHistory.length === 0) return [];
    const currentLayer = outfitHistory[currentOutfitIndex];
    return currentLayer ? Object.keys(currentLayer.poseImages) : [];
  }, [outfitHistory, currentOutfitIndex]);

  const handleStartSession = async (userFile: File, garmentFile: File) => {
    if (!user) {
      setIsUserProfileOpen(true);
      return;
    }

    // Wait for stats to load before allowing try-on
    if (isLoadingStats) {
      setError('جاري التحقق من الرصيد، يرجى الانتظار...');
      return;
    }

    if (userStats && userStats.tryOnCount >= userStats.allowedTryOnCount) {
      setIsSubscriptionModalOpen(true);
      return;
    }

    // Set loading state
    setIsLoading(true);
    setLoadingMessage('Preparing fitting room...');
    setError(null);

    try {
      // 1. Set the User Image as the "Model"
      // Convert File to URL for display
      const userImgUrl = URL.createObjectURL(userFile);
      setModelImageUrl(userImgUrl);

      // 2. Prepare the Custom Garment Info
      const customGarment: WardrobeItem = {
        id: `custom-${Date.now()}`,
        name: 'Custom Item',
        url: URL.createObjectURL(garmentFile),
        price: 0,
        description: 'Uploaded by user'
      };

      // 3. Immediately trigger the try-on
      // We need to wait a tick for modelImageUrl to Set? No, we can pass userImgUrl directly.
      setLoadingMessage('Processing... (this may take up to a minute)');

      // Call the VTON service
      // Note: generateVirtualTryOnImage expects (modelUrl, garmentFile)
      // Ensure userImgUrl is accessible or pass the File if the service supported it. 
      // falService's generateVirtualTryOnImageFal likely handles this. 
      // If it takes a URL, Blob URLs work if the service handles them (it might need a real upload if it's external).
      // Assuming the Fal service can take the file if we modify it or if it uploads internally.
      // Actually, looking at previous code, `generateVirtualTryOnImage` takes (modelImageUrl, garmentFile).
      // We'll pass userImgUrl. If Fal needs a public URL, the service layer handles upload.

      const newImageUrl = await generateVirtualTryOnImage(userFile, garmentFile);

      // 4. Update History
      const initialLayer: OutfitLayer = {
        garment: null,
        poseImages: { [POSE_INSTRUCTIONS[0]]: userImgUrl }
      };
      const newLayer: OutfitLayer = {
        garment: customGarment,
        poseImages: { [POSE_INSTRUCTIONS[0]]: newImageUrl }
      };

      setOutfitHistory([initialLayer, newLayer]);
      setCurrentOutfitIndex(1);
      setCurrentPoseIndex(0);

      // 5. Update Stats & History (Non-blocking)
      setUserStatsState(prev => prev ? ({ ...prev, tryOnCount: prev.tryOnCount + 1 }) : { tryOnCount: 1, allowedTryOnCount: 3, isSubscribed: false });
      Promise.all([
        incrementTryOnCount(user.uid),
        saveTryOnResult(user.uid, newImageUrl, customGarment)
      ]).catch(err => console.error("Background DB Saved Failed:", err));

      // 6. Update Wardrobe (Optional)
      setWardrobe(prev => [customGarment, ...prev]);

    } catch (err) {
      console.error('Start Session Error:', err);
      setError(getFriendlyErrorMessage(err, 'Failed to start try-on'));
      setModelImageUrl(null); // Reset if failed so they can try again
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };


  const handleStartOver = () => {
    setModelImageUrl(null);
    setOutfitHistory([]);
    setCurrentOutfitIndex(0);
    setIsLoading(false);
    setLoadingMessage('');
    setError(null);
    setCurrentPoseIndex(0);
    setIsSheetCollapsed(false);
    setWardrobe(defaultWardrobe);
  };

  /* REMOVED handleGarmentSelect and others if they were duplicated or modified logic is sufficient. 
     Actually, we keep handleGarmentSelect for subsequent try-ons from the panel. */

  const handleGarmentSelect = useCallback(async (garmentFile: File, garmentInfo: WardrobeItem) => {
    if (!displayImageUrl || isLoading) return;

    const nextLayer = outfitHistory[currentOutfitIndex + 1];
    if (nextLayer && nextLayer.garment?.id === garmentInfo.id) {
      setCurrentOutfitIndex(prev => prev + 1);
      setCurrentPoseIndex(0);
      return;
    }

    if (!user) {
      setIsUserProfileOpen(true);
      return;
    }

    // Wait for stats to load before allowing try-on
    if (isLoadingStats) {
      setError('جاري التحقق من الرصيد، يرجى الانتظار...');
      return;
    }

    if (userStats && userStats.tryOnCount >= userStats.allowedTryOnCount) {
      setIsSubscriptionModalOpen(true);
      return;
    }

    setError(null);
    setIsLoading(true);
    setLoadingMessage(`Adding ${garmentInfo.name}...`);

    try {
      const newImageUrl = await generateVirtualTryOnImage(displayImageUrl!, garmentFile);

      // Update Local UI first so user sees the result immediately
      const currentPoseInstruction = POSE_INSTRUCTIONS[currentPoseIndex];
      const newLayer: OutfitLayer = {
        garment: garmentInfo,
        poseImages: { [currentPoseInstruction]: newImageUrl }
      };

      setOutfitHistory(prevHistory => {
        const newHistory = prevHistory.slice(0, currentOutfitIndex + 1);
        return [...newHistory, newLayer];
      });
      setCurrentOutfitIndex(prev => prev + 1);
      setUserStatsState(prev => prev ? ({ ...prev, tryOnCount: prev.tryOnCount + 1 }) : { tryOnCount: 1, allowedTryOnCount: 3, isSubscribed: false });

      // Background DB Update
      if (user) {
        Promise.all([
          incrementTryOnCount(user.uid),
          saveTryOnResult(user.uid, newImageUrl, garmentInfo)
        ]).catch(e => console.warn("App: DB Update/History Save failed:", e));
      }

      setWardrobe(prev => {
        if (prev.find(item => item.id === garmentInfo.id)) {
          return prev;
        }
        return [...prev, garmentInfo];
      });
    } catch (err) {
      console.error('Error in generateVirtualTryOnImage:', err);
      const errorMessage = getFriendlyErrorMessage(err, 'Failed to apply clothing item');
      setError(errorMessage);

      if (errorMessage.includes('API') || errorMessage.includes('Key')) {
        setError(errorMessage + ' Please check your API Key.');
      }
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, [displayImageUrl, isLoading, currentPoseIndex, outfitHistory, currentOutfitIndex, user, userStats, isLoadingStats]);

  const handleRemoveLastGarment = () => {
    if (currentOutfitIndex > 0) {
      setCurrentOutfitIndex(prevIndex => prevIndex - 1);
      setCurrentPoseIndex(0);
    }
  };

  const handlePoseSelect = useCallback(async (newIndex: number) => {
    if (isLoading || outfitHistory.length === 0 || newIndex === currentPoseIndex) return;

    const poseInstruction = POSE_INSTRUCTIONS[newIndex];
    const currentLayer = outfitHistory[currentOutfitIndex];

    if (currentLayer.poseImages[poseInstruction]) {
      setCurrentPoseIndex(newIndex);
      return;
    }

    const baseImageForPoseChange = Object.values(currentLayer.poseImages)[0];
    if (!baseImageForPoseChange) return;

    setError(null);
    setIsLoading(true);
    setLoadingMessage(`Changing pose...`);

    const prevPoseIndex = currentPoseIndex;
    setCurrentPoseIndex(newIndex);

    // Wait for stats to load before allowing pose change
    if (isLoadingStats) {
      setError('جاري التحقق من الرصيد، يرجى الانتظار...');
      setCurrentPoseIndex(prevPoseIndex);
      return;
    }

    if (userStats && userStats.tryOnCount >= userStats.allowedTryOnCount) {
      setIsSubscriptionModalOpen(true);
      setCurrentPoseIndex(prevPoseIndex);
      return;
    }

    try {
      const newImageUrl = await generatePoseVariation(baseImageForPoseChange, poseInstruction);

      // Update UI immediately
      setOutfitHistory(prevHistory => {
        const newHistory = [...prevHistory];
        if (newHistory[currentOutfitIndex]) {
          newHistory[currentOutfitIndex] = {
            ...newHistory[currentOutfitIndex],
            poseImages: {
              ...newHistory[currentOutfitIndex].poseImages,
              [poseInstruction]: newImageUrl
            }
          };
        }
        return newHistory;
      });
      setUserStatsState(prev => prev ? ({ ...prev, tryOnCount: prev.tryOnCount + 1 }) : { tryOnCount: 1, allowedTryOnCount: 3, isSubscribed: false });
      console.log("App: Pose updated in history locally.");

      // Background DB update
      if (user) {
        incrementTryOnCount(user.uid).catch(e => console.warn("App: DB Update for pose failed:", e));
      }
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Failed to change pose'));
      setCurrentPoseIndex(prevPoseIndex);
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, [currentPoseIndex, outfitHistory, isLoading, currentOutfitIndex, user, userStats, isLoadingStats]);

  const handleTestIncrement = async () => {
    if (!user) {
      setIsUserProfileOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      setLoadingMessage('Simulating try-on recording...');

      // Update local state first for immediate feedback
      setUserStatsState(prev => prev ? ({
        ...prev,
        tryOnCount: prev.tryOnCount + 1
      }) : { tryOnCount: 1, allowedTryOnCount: 3, isSubscribed: false });

      // Persist to Firestore (will be queued if offline)
      await incrementTryOnCount(user.uid);

      setLoadingMessage('Saved successfully! (Syncing in background if offline)');
      setTimeout(() => setLoadingMessage(''), 2000);
    } catch (err) {
      console.error("Test Increment Failed:", err);
      setError('Test recording failed. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoHome = () => {
    setModelImageUrl(null);
    setOutfitHistory([]);
    setCurrentOutfitIndex(0);
    setError(null);
  };

  const viewVariants = {
    initial: { opacity: 0, y: 15 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -15 },
  };

  return (
    <div className="font-sans min-h-screen bg-white">
      <Navigation
        onUserClick={() => setIsUserProfileOpen(true)}
        onHomeClick={handleGoHome}
        userStats={userStats}
      />

      <AnimatePresence>
        <UserProfile
          key="user-profile"
          isOpen={isUserProfileOpen}
          onClose={() => setIsUserProfileOpen(false)}
          favoriteProducts={wardrobe.filter(item => favorites.has(item.id))}
          onToggleFavorite={handleToggleFavorite}
          userStats={userStats}
          isLoadingStats={isLoadingStats}
          onOpenSubscription={() => setIsSubscriptionModalOpen(true)}
          onTestIncrement={handleTestIncrement}
          onOpenAdminDashboard={() => {
            setIsUserProfileOpen(false);
            setIsAdminPageOpen(true);
          }}
          onGarmentSelect={handleGarmentSelect}
          onHistoryItemSelect={(historyItem) => {
            setViewingHistoryItem(historyItem);
          }}
          isLoading={isLoading}
        />
        <AdminDashboard
          key="admin-dashboard"
          isOpen={isAdminPageOpen}
          onClose={() => setIsAdminPageOpen(false)}
        />
        <SubscriptionModal
          key="subscription-modal"
          isOpen={isSubscriptionModalOpen}
          onClose={() => setIsSubscriptionModalOpen(false)}
          onSuccess={async (credits: number) => {
            if (user) {
              await addUserCredits(user.uid, credits);
              setUserStatsState(prev => prev ? ({
                ...prev,
                allowedTryOnCount: prev.allowedTryOnCount + credits,
                isSubscribed: true
              }) : { tryOnCount: 0, allowedTryOnCount: credits, isSubscribed: true });
              setIsSubscriptionModalOpen(false);
            }
          }}
        />
        {viewingHistoryItem && (
          <HistoryViewPage
            key="history-view"
            item={viewingHistoryItem}
            onClose={() => setViewingHistoryItem(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {!modelImageUrl ? (
          <motion.div
            key="start-screen"
            className="w-screen min-h-screen flex items-start sm:items-center justify-center bg-gray-50 p-4 pb-20"
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <StartScreen
              onStartTryOn={handleStartSession}
              onRequireAuth={() => setIsUserProfileOpen(true)}
              isLoadingStats={isLoadingStats}
            />
          </motion.div>
        ) : (
          <motion.div
            key="main-app"
            className="relative flex flex-col min-h-screen md:h-[calc(100vh-64px)] bg-white overflow-x-hidden"
            variants={viewVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          >
            <main
              className="flex-grow relative flex flex-col md:flex-row overflow-y-auto md:overflow-hidden bg-white"
            >
              {/* Image Area */}
              <div
                className="w-full h-[70vh] md:h-full flex-shrink-0 flex items-center justify-center bg-white relative border-b md:border-b-0"
              >
                <Canvas
                  displayImageUrl={displayImageUrl}
                  onStartOver={handleStartOver}
                  isLoading={isLoading}
                  loadingMessage={loadingMessage}
                  onSelectPose={handlePoseSelect}
                  poseInstructions={POSE_INSTRUCTIONS}
                  currentPoseIndex={currentPoseIndex}
                  availablePoseKeys={availablePoseKeys}
                />
              </div>

              {/* Controls Area (Outfit & History) */}
              <aside
                className="w-full md:w-1/3 md:max-w-sm bg-gray-50/10 md:bg-white flex flex-col md:border-s border-gray-200/60"
              >
                <div className="p-4 md:p-6 pb-32 flex-grow flex flex-col gap-8">
                  {error && (
                    <div className="bg-red-100 border-s-4 border-red-500 text-red-700 p-4 mb-4 rounded-md" role="alert">
                      <p className="font-bold">Error</p>
                      <p>{error}</p>
                    </div>
                  )}
                  <OutfitStack
                    outfitHistory={activeOutfitLayers}
                    onRemoveLastGarment={handleRemoveLastGarment}
                    onBuyNow={() => { }}
                  />
                  <HistoryPanel
                    onGarmentSelect={handleGarmentSelect}
                    onHistoryItemSelect={(historyItem) => {
                      setViewingHistoryItem(historyItem);
                    }}
                    isLoading={isLoading}
                  />
                </div>
              </aside>
            </main>
            <AnimatePresence>
              {isLoading && isMobile && (
                <motion.div
                  className="fixed inset-0 bg-white/80 backdrop-blur-md flex flex-col items-center justify-center z-50"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Spinner />
                  {loadingMessage && (
                    <p className="text-lg font-serif text-gray-700 mt-4 text-center px-4">{loadingMessage}</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
      <Footer isOnDressingScreen={!!modelImageUrl} />
    </div>
  );
};

export default App;
